import React from 'react';

import {StorableItemType} from 'constants/ModelTypeConstants';
import {Storable} from 'constants/SampleConstants';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {AttachDropdownOuterWrapper} from 'ui-library/attach-dropdown/AttachDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ElementMouseEvent} from 'util/Types';

import LocationSearchDropdownSearcher from './LocationSearchDropdownSearcher';
import LocationSearchDropdownTree from './LocationSearchDropdownTree';

export interface LocationSearchDropdownProps {
  registryId: string;
  query: string;
  storableTypes: Array<StorableItemType>;
  onSetData: (typedValue: TypedValue | null) => void;
  storableSchemaId?: string;
  includeArchived: boolean;
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
}

interface State {
  results: Array<Storable>;
  errorMessage: string | null;
  searching: boolean;
}

const cleanState: State = {results: [], errorMessage: null, searching: false};

/**
 * This component takes a query for storables to pass to LocationSearchDropdownSearcher, keeps track of the
 * searching state and search results, and passes the search results for LocationSearchDropdownTree to display
 * in a tree.
 */
class LocationSearchDropdown extends React.Component<LocationSearchDropdownProps> {
  override state: State = cleanState;

  private handleSearchEnd = (searchResults: {
    query: string;
    results: Array<Storable>;
    errorMessage: string | null;
  }): void => {
    this.setState({
      results: searchResults.results,
      errorMessage: searchResults.errorMessage,
      searching: false,
    });
  };

  private renderResults = (): JSX.Element | null => {
    if (this.state.searching) {
      return <CenteredHorizontalThrobber />;
    } else if (this.props.query !== '') {
      if (this.state.results.length) {
        return (
          <LocationSearchDropdownTree
            results={this.state.results}
            registryId={this.props.registryId}
            storableTypes={this.props.storableTypes}
            onSetData={this.props.onSetData}
            includeArchived={this.props.includeArchived}
            isSearchResults={true}
            storablesToBeMoved={this.props.storablesToBeMoved}
            storableSchemaId={this.props.storableSchemaId}
          />
        );
      } else if (this.state.errorMessage) {
        return <NoticeMessage message={this.state.errorMessage} />;
      } else {
        return <NoticeMessage message="No results found." />;
      }
    } else {
      return (
        <LocationSearchDropdownTree
          results={this.state.results}
          registryId={this.props.registryId}
          storableTypes={this.props.storableTypes}
          onSetData={this.props.onSetData}
          isSearchResults={false}
          includeArchived={this.props.includeArchived}
          storablesToBeMoved={this.props.storablesToBeMoved}
          storableSchemaId={this.props.storableSchemaId}
        />
      );
    }
  };

  private onMouseDown = (e: ElementMouseEvent): void => {
    // Prevent the click event from causing the `AttachDropdown` to close. This happens when the click is
    // processed by the `AttachDropdownOption` and triggers an `onBlur` event in AbsoluteDropdownContainer.
    e.preventDefault();
  };

  override render(): JSX.Element {
    // LocationSearchDropdown can be thought of as a specialized AttachDropdown that displays results in a
    // different format than a regular list. As a result, it should have the same style as an
    // AttachDropdown, which AttachDropdownOuterWrapper applies.
    // If we don't specify it here, width defaults to 300px, which is narrow. Width of location picker in
    // the move modal is 544px, but that looks too wide in the notebook context.
    return (
      <AttachDropdownOuterWrapper dropup={false} forMenu={false} width="420px">
        <div onMouseDown={this.onMouseDown}>
          {this.renderResults()}
          <LocationSearchDropdownSearcher
            registryId={this.props.registryId}
            query={this.props.query}
            storableSchemaId={this.props.storableSchemaId}
            searchableTypes={this.props.storableTypes}
            includeArchived={this.props.includeArchived}
            onSearchStart={(): void => {
              this.setState({searching: true});
            }}
            onSearchEnd={this.handleSearchEnd}
          />
        </div>
      </AttachDropdownOuterWrapper>
    );
  }
}

interface NoticeMessageProps {
  message: string;
}

// NoticeMessages should have the same style as attachDropdown-notice messages because LocationSearchDropdown
// follows the same style as AttachDropdown.
function NoticeMessage(props: NoticeMessageProps): JSX.Element {
  return <li className="attachDropdown-notice">{props.message}</li>;
}

export default LocationSearchDropdown;
